import { ApiError, demoApi } from "@/api/client";
import { withDrainLock, DRAIN_LOCKS } from "./drainLock";
import { createPersistentQueue, type PersistentQueue, type QueueItem } from "./persistentQueue";
import { registerDrainTriggers } from "./drainTriggers";

/**
 * A vendor->account teaching write that couldn't reach the server yet
 * (build task #3: "queue the mapping lesson too, so the vendor->account
 * teaching survives an offline scan"). Deliberately its own tiny queue
 * rather than folded into the entry outbox: a mapping write can be pending
 * for reasons unrelated to any single entry (see `outbox.ts`'s drain, which
 * also enqueues here when a scanned entry's mapping write fails offline).
 */
export interface QueuedMapping extends QueueItem {
  vendorId: string;
  accountId: string;
}

const DB_NAME = "easyfarm-offline-mappings";
const STORE_NAME = "mappings";

function newClientId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

class MappingQueueManager {
  private storePromise: Promise<PersistentQueue<QueuedMapping>> | null = null;
  private draining = false;

  private getStore(): Promise<PersistentQueue<QueuedMapping>> {
    if (!this.storePromise) {
      this.storePromise = createPersistentQueue<QueuedMapping>(DB_NAME, STORE_NAME, {
        clientId: "__probe__",
        vendorId: "",
        accountId: "",
        queuedAt: 0,
      });
    }
    return this.storePromise;
  }

  async enqueue(mapping: { vendorId: string; accountId: string }): Promise<void> {
    const store = await this.getStore();
    await store.add({ clientId: newClientId(), ...mapping, queuedAt: Date.now() });
  }

  /**
   * Replay pending mapping writes in order on reconnect. Mirrors
   * `outbox.ts`'s `drain`: stop (without dropping anything) on a genuine
   * offline failure so the next 'online' event resumes from the same item;
   * warn and move on for anything else, since one rejected mapping must not
   * wedge every later one — see `outbox.ts` for the identical trade-off.
   */
  /** Used by `registerDrainTriggers` to decide whether to keep retrying. */
  async list(): Promise<QueuedMapping[]> {
    return (await this.getStore()).all();
  }

  async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    try {
      // ACROSS TABS, for the reason in offline/drainLock.ts. Confirming one mapping twice is
      // harmless where its siblings' duplicates are not — the server upserts — so this is
      // consistency rather than a measured defect, and the cheaper thing than leaving one
      // queue of the three able to double-send whatever it grows into later.
      await withDrainLock(DRAIN_LOCKS.mappings, () => this.drainOnce());
    } finally {
      this.draining = false;
    }
  }

  /** One pass over the queue. Only ever called while the origin-wide mapping lock is held. */
  private async drainOnce(): Promise<void> {
    {
      const store = await this.getStore();
      const items = await store.all();
      for (const item of items) {
        try {
          await demoApi.confirmMapping({ vendorId: item.vendorId, accountId: item.accountId });
        } catch (err) {
          if (err instanceof ApiError && err.status === 0) return;
          console.warn("A queued vendor mapping was rejected by the server; leaving it queued.", err);
          continue;
        }
        await store.remove(item.clientId);
      }
    }
  }
}

export const mappingQueue = new MappingQueueManager();

registerDrainTriggers(mappingQueue);
